/**
 * One aggregated read for the platform dashboard (Dashboard v5).
 *
 * The page needs figures from thirteen endpoints that were never designed to be
 * asked together, so this module fans out in parallel and normalises what comes
 * back. Every call is settled independently: a failing endpoint yields `null`
 * for its slice rather than blanking the page, and the view renders "—" for a
 * metric it could not read. A zero and an unavailable number are different
 * claims and must not look the same.
 *
 * v5 shows one module at a time, but the fan-out stays whole-page: the three
 * views share a range and switching between them is a tab click, so fetching
 * per-view would put a network round trip behind every switch.
 */
import { api } from './api';
import type { ReviewCenterData } from '../types/review-center.types';
import type { ErrorLogDashboardStats } from '../types';
import type { DashboardData as AuditDashboard } from '../types/log-transaction.types';
import type { DashboardData as NotificationDashboard } from './notification-analytics.service';
import type { BackgroundJobDashboard, BackgroundJobRun } from '../types/background-job.types';
import type { EvalRun } from '../types/ai-run.types';

/** Feedback rollup shown on the platform dashboard's Feedback strip. Shaped from
 *  the dashboard endpoint's `feedback` block (the standalone feedback.service was
 *  removed; this is the only consumer of the type). */
export interface FeedbackDashboard {
  overview?: {
    total?: number;
    averageRating?: number;
    positive?: number;
    neutral?: number;
    negative?: number;
  } | null;
  trend?: Array<{ count: number }>;
  byTargetType?: Array<{ targetType: string; count: number }>;
}

const BASE = '/api/v1/dashboard';

export type RangeId = 'today' | 'week' | 'month';

/** Dashboard tab → the `timeRange` the analytics endpoints understand. */
const TIME_RANGE: Record<RangeId, string> = { today: 'today', week: '7d', month: '30d' };
/*
 * The other four range vocabularies this page used to translate — review `days`,
 * audit `timeline`, notification `dateRange`, error-log `timeRange` — now live
 * in one table on the server. Sending the wrong spelling silently blanked a
 * panel, so having a single mapping is the point.
 */

// ---------------------------------------------------------------------------
// Endpoint payloads — only the fields this page reads
// ---------------------------------------------------------------------------

export interface CountItem {
  count: number;
  isMenu: boolean;
  route: string | null;
  displayName?: string;
}
export interface CountsPayload {
  platform: Record<string, CountItem>;
  application: Record<string, CountItem>;
}

export interface OverviewStats {
  totalPromptExecutions: number;
  totalTokensUsed: number;
  totalCost: number;
  activeUsers: number;
  avgResponseTime: number;
  successRate: number;
}
export interface OverviewPayload {
  currentPeriod: OverviewStats;
  previousPeriod: OverviewStats;
  change: { executions: number; tokens: number; cost: number; users: number };
  topModel: string | null;
  topPromptType: string | null;
  peakHour: number | null;
}

/** One row of `performance.byModel` — avg and success only; no percentiles. */
export interface ModelPerf {
  model: string;
  avgResponseTime: number;
  calls: number;
  successRate: number;
}

/** A latency bucket from the `$bucket` stage: 0-1s, 1-2s, 2-5s, 5-10s, 10s+. */
export interface LatencyBucket {
  range: string;
  count: number;
  percentage: number;
}

export interface SlowCall {
  executionId: string;
  timestamp: string | null;
  responseTime: number;
  promptName: string | null;
  model: string | null;
  userName: string | null;
  tokens: number;
}

export interface PerformancePayload {
  summary: {
    avgResponseTime: number;
    p50ResponseTime: number;
    p95ResponseTime: number;
    p99ResponseTime: number;
    successRate: number;
    errorRate: number;
  };
  /* Both of these have always been in the response; they were simply never
     typed, so the page could not read them. Nothing new is requested. */
  responseTimeDistribution: LatencyBucket[];
  slowestCalls: SlowCall[];
  byHour: { hour: number; avgResponseTime: number; calls: number; errorRate: number }[];
  byModel: ModelPerf[];
}

export interface TopPromptItem {
  promptId: string;
  promptName: string;
  promptType: string | null;
  llm: string | null;
  totalExecutions: number;
  totalTokens: number;
  totalCost: number;
  avgResponseTime: number;
  successRate: number;
}
export interface PromptAnalyticsPayload {
  summary: {
    totalPrompts: number;
    activePrompts: number;
    totalExecutions: number;
    avgExecutionsPerPrompt: number;
  };
  topPrompts: TopPromptItem[];
  byType: { promptType: string; count: number; totalExecutions: number; percentage: number }[];
}

export interface UnitUsagePayload {
  summary: { totalUnits: number; activeUnits: number; totalCalls: number; totalCost: number };
  topUnits: {
    unitId: string;
    unitName: string;
    districtName: string | null;
    activeUsers: number;
    totalCalls: number;
  }[];
}

export interface UserActivityPayload {
  summary: { totalUsers: number; activeUsers: number; newUsers: number; inactiveUsers: number };
  activityTrend: { timestamp: string; activeUsers: number; newUsers: number; totalCalls: number }[];
}

/** Row counts the counts endpoint does not cover, read from list pagination. */
export interface ExtraTotals {
  ministries: number | null;
  evals: number | null;
  aiModels: number | null;
}

/** Exactly what `GET /api/v1/dashboard/platform` answers with. */
interface PlatformPayload {
  range: string;
  generatedAt: string | null;
  counts: CountsPayload | null;
  overview: OverviewPayload | null;
  performance: PerformancePayload | null;
  prompts: PromptAnalyticsPayload | null;
  unitUsage: UnitUsagePayload | null;
  userActivity: UserActivityPayload | null;
  review: ReviewCenterData | null;
  errors: ErrorLogDashboardStats | null;
  audit: AuditDashboard | null;
  notifications: NotificationDashboard | null;
  jobs: BackgroundJobDashboard | null;
  jobRuns: BackgroundJobRun[] | null;
  evalRuns: EvalRun[] | null;
  feedback: FeedbackDashboard | null;
  totals: { ministries: number | null; evals: number | null; aiModels: number | null } | null;
  unavailable: string[] | null;
}

export interface PlatformDashboardData {
  range: RangeId;
  fetchedAt: string;
  counts: CountsPayload | null;
  overview: OverviewPayload | null;
  performance: PerformancePayload | null;
  prompts: PromptAnalyticsPayload | null;
  unitUsage: UnitUsagePayload | null;
  userActivity: UserActivityPayload | null;
  review: ReviewCenterData | null;
  errors: ErrorLogDashboardStats | null;
  audit: AuditDashboard | null;
  notifications: NotificationDashboard | null;
  jobs: BackgroundJobDashboard | null;
  jobRuns: BackgroundJobRun[] | null;
  evalRuns: EvalRun[] | null;
  feedback: FeedbackDashboard | null;
  totals: ExtraTotals;
  /** Endpoints that failed, so the page can say so instead of implying zero. */
  unavailable: string[];
}

// ---------------------------------------------------------------------------

/** Unwraps `{ data: { data: T } }` and swallows failures into `null`. */
const get = async <T>(path: string, params?: Record<string, unknown>): Promise<T | null> => {
  try {
    const res = await api.get<{ data: T }>(`${BASE}${path}`, { params });
    return res.data?.data ?? null;
  } catch (err) {
    console.warn(`platform dashboard: ${path} failed`, err);
    return null;
  }
};

/**
 * Guarantees a list slice is an array or null — never a truthy non-array.
 *
 * A list endpoint that answers with an object where the page expects rows (an
 * error envelope, a changed contract, a `data: {}` on an empty result) used to
 * reach the view as a truthy value, and the first `.filter` on it threw during
 * render. One bad slice took the whole dashboard blank. Coercing here means the
 * worst case is one panel saying it has nothing to show.
 */
const listOf = <T>(v: T[] | null | undefined): T[] | null =>
  Array.isArray(v) ? v : null;

export const platformDashboardService = {
  /**
   * One request for the whole page.
   *
   * This used to fan out seventeen concurrent calls and compose them here. Six
   * of them returned range-invariant data and were re-fetched on every range
   * switch, and three fetched a page of records purely to read one integer off
   * its pagination block — so a Today → 7 days → 30 days sequence cost 51
   * requests. `GET /api/v1/dashboard/platform` now does that composition
   * server-side, still concurrently, and answers with the same field names this
   * page already renders.
   *
   * The per-area endpoints are untouched and still serve their own screens; the
   * consolidated one delegates to the same services, so the two cannot disagree.
   */
  load: async (range: RangeId): Promise<PlatformDashboardData> => {
    const payload = await get<PlatformPayload>('/platform', { timeRange: TIME_RANGE[range] });

    if (!payload) {
      /* The single call failed, so nothing is known. Every slice is null and
         every panel says so — which is the truth, and is different from each
         slice independently reporting zero. */
      return {
        range,
        fetchedAt: new Date().toISOString(),
        counts: null, overview: null, performance: null, prompts: null,
        unitUsage: null, userActivity: null, review: null, errors: null,
        audit: null, notifications: null, jobs: null, feedback: null,
        jobRuns: null, evalRuns: null,
        totals: { ministries: null, evals: null, aiModels: null },
        unavailable: ['dashboard'],
      };
    }

    /* The server already isolates its own slices and names the failures, but a
       slice can still arrive shaped wrongly — a contract change, or a payload
       that serialised as `{}`. `shaped` keeps the rule the page depends on: a
       slice that cannot answer is null, never an empty object that would read
       as a quiet period. */
    const shaped = <T,>(v: T | null | undefined, key: keyof T): T | null =>
      v && (v as Record<string, unknown>)[key as string] !== undefined ? v : null;

    const counts = shaped(payload.counts, 'platform');
    const overview = shaped(payload.overview, 'currentPeriod');
    const performance = shaped(payload.performance, 'summary');
    const prompts = shaped(payload.prompts, 'summary');
    const unitUsage = shaped(payload.unitUsage, 'summary');
    const userActivity = shaped(payload.userActivity, 'summary');
    const errors = shaped(payload.errors, 'summary');
    const audit = shaped(payload.audit, 'overview');
    const notifications = shaped(payload.notifications, 'channelStats');
    const jobs = shaped(payload.jobs, 'totalJobs');
    const feedback = shaped(payload.feedback, 'overview');

    /* Start from what the server reported, then add anything that arrived
       misshapen so the banner names it too. */
    const unavailable = [...(payload.unavailable ?? [])];
    const note = (v: unknown, label: string) => {
      if (!v && !unavailable.includes(label)) unavailable.push(label);
    };
    note(counts, 'counts');
    note(overview, 'overview');
    note(performance, 'performance');
    note(prompts, 'prompt analytics');
    note(unitUsage, 'unit usage');
    note(userActivity, 'user activity');
    note(payload.review, 'review center');
    note(errors, 'error logs');
    note(audit, 'audit logs');
    note(notifications, 'notifications');
    note(jobs, 'background jobs');
    note(feedback, 'feedback');

    return {
      range,
      fetchedAt: payload.generatedAt ?? new Date().toISOString(),
      counts, overview, performance, prompts, unitUsage, userActivity,
      review: payload.review ?? null,
      errors, audit, notifications, jobs, feedback,
      jobRuns: listOf(payload.jobRuns),
      evalRuns: listOf(payload.evalRuns),
      totals: {
        ministries: payload.totals?.ministries ?? null,
        evals: payload.totals?.evals ?? null,
        aiModels: payload.totals?.aiModels ?? null,
      },
      unavailable,
    };
  },
};

// ---------------------------------------------------------------------------
// Formatting helpers — shared so the view never re-implements them
// ---------------------------------------------------------------------------

/** `null`/`undefined` become an em dash; a real 0 stays "0". */
export const num = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(n) ? '—' : Math.round(n).toLocaleString();

/**
 * 8,126 → "8.1k". Keeps big counts readable at display size.
 *
 * Thresholds are applied to the ROUNDED value so adjacent numbers cannot format
 * inconsistently: 9,999 used to render "10.0k" while 10,000 rendered "10k", and
 * 999,999 rendered "1000k" instead of "1.0M". Negatives keep their sign rather
 * than falling through to the raw-number branch.
 */
export const compact = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 999_500) return `${sign}${(a / 1_000_000).toFixed(1)}M`;
  if (a >= 9_995) return `${sign}${Math.round(a / 1_000)}k`;
  if (a >= 1_000) return `${sign}${(a / 1_000).toFixed(1)}k`;
  return `${sign}${Math.round(a).toLocaleString()}`;
};

/** A signed percentage, or null when there is nothing to compare against. */
export const pct = (n: number | null | undefined): string | null =>
  n === null || n === undefined || !Number.isFinite(n)
    ? null
    : `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(n >= 10 || n <= -10 ? 0 : 1)}%`;

/** A bare percentage for a rate that is already 0–100. */
export const rate = (n: number | null | undefined, dp = 1): string =>
  n === null || n === undefined || !Number.isFinite(n) ? '—' : `${n.toFixed(dp)}%`;

export const secs = (n: number | null | undefined): string =>
  n === null || n === undefined || !Number.isFinite(n) ? '—' : `${n.toFixed(n < 10 ? 2 : 1)}s`;

/** Seconds as a duration a person reads: 382 → "6m 22s". */
export const dur = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  if (n < 60) return `${n.toFixed(n < 10 ? 1 : 0)}s`;
  const m = Math.floor(n / 60);
  return `${m}m ${String(Math.round(n - m * 60)).padStart(2, '0')}s`;
};

/**
 * Currency, with thousands separators — `$124610` was rendering unseparated
 * while every other figure on the page used them. Under a cent reads "<$0.01"
 * rather than "$0.00", and a negative keeps its sign outside the symbol.
 */
export const money = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a > 0 && a < 0.01) return `${sign}<$0.01`;
  /* Cents only under $10. The switch used to sit at $100, so the same metric
     rendered "$59.00" for one range and "$413" for the next — a formatting
     change that read as a precision change in the underlying figure. */
  const dp = a < 10 ? 2 : 0;
  return `${sign}$${a.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp })}`;
};

/** A minute count as "4m" / "6h" / "2d". */
const span = (m: number): string =>
  m < 60 ? `${m}m` : m < 1440 ? `${Math.floor(m / 60)}h` : `${Math.floor(m / 1440)}d`;

/** An ISO timestamp as "4m" / "6h" / "2d" ago, or "—" when absent. */
export const ago = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const m = Math.round((Date.now() - t) / 60000);
  return m < 1 ? 'now' : span(m);
};

/**
 * A FUTURE ISO timestamp as "in 9m". Passing one of these to `ago` gives a
 * negative minute count, which falls through its `< 1` branch and renders
 * "now" — so a job due in an hour reported itself as already running.
 */
export const until = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '—';
  const m = Math.round((t - Date.now()) / 60000);
  return m < 1 ? 'due now' : `in ${span(m)}`;
};

/** Reads a count out of the counts payload without caring which bucket it is in. */
export const countOf = (counts: CountsPayload | null, key: string): number | null => {
  if (!counts) return null;
  const item = counts.platform?.[key] ?? counts.application?.[key];
  return item ? item.count : null;
};
